import {
  Delete,
  Download,
  FindInPage,
  Search,
  PictureInPicture,
} from '@mui/icons-material';
import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { getFetch } from '../hooks/useFetch';
import { IDocument } from '../Interfaces/IDocument';
import { IDocumentDownloaded } from '../Interfaces/IDocumentDownloaded';
import { IMIMEType } from '../Interfaces/IMIMEType';
import { useSidepanelDocumentContext } from '../LegalTransaction/LegalTransactionEdit';
import { DocumentDownloadedDialog } from './DocumentAssistant/DocumentDownloadedDialog';
import { DocumentPreviewDialog } from './DocumentAssistant/DocumentPreviewDialog';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ILegalClient } from '../Interfaces/ILegalClient';

interface IProps {
  documentObject: IDocument;
  documentArray: IDocument[];
  setDocumentArray: (newDocuments: IDocument[]) => void;
  mimeTypeArray: IMIMEType[];
  allowRemove: boolean;
  downloadedArray?: IDocumentDownloaded[];
  justButtons?: boolean;
  enableSidepanel?: boolean;
  isStandarddocument?: boolean;
  isSendEmailPossible?: boolean;
  checkedBoxes?: number[];
  setCheckedBoxes?: React.Dispatch<React.SetStateAction<number[]>>;
  assignedLegalClients: ILegalClient[];
}

export const base64Download = (
  localDocumentObject: IDocument,
  mimeTypes: IMIMEType[]
) => {
  const currentMimeType = mimeTypes.find(
    x => x.idMIMEType === localDocumentObject.idMIMEType
  );

  if (localDocumentObject.Data && currentMimeType) {
    const byteCharacters = atob(localDocumentObject.Data);
    // Each character's code point (charCode) will be the value of the byte.
    // We can create an array of byte values by applying this using the .charCodeAt method for each character in the string.
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    //You can convert this array of byte values into a real typed byte array by passing it to the Uint8Array constructor.
    const byteArray = new Uint8Array(byteNumbers);
    // This in turn can be converted to a BLOB by wrapping it in an array and passing it to the Blob constructor.
    const blob = new Blob([byteArray], { type: currentMimeType.MIMEType });
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = localDocumentObject.FileName;
    a.click();
  }
};

export const DocumentRow: React.FC<IProps> = ({
  documentObject,
  documentArray,
  setDocumentArray,
  mimeTypeArray,
  allowRemove,
  downloadedArray,
  justButtons,
  enableSidepanel = false,
  isStandarddocument = false,
  isSendEmailPossible = true,
  checkedBoxes,
  setCheckedBoxes,
  assignedLegalClients,
}) => {
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();

  const [localDocumentObject, setLocalDocumentObject] =
    useState(documentObject);
  const [isOpenDownloaded, setIsOpenDownloaded] = useState(false);
  const [isOpenPreview, setisOpenPreview] = useState(false);

  const handleDownload = async () => {
    if (localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  const handleOpenPreview = () => {
    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        setLocalDocumentObject
      );
    }
    setisOpenPreview(true);
  };

  const handleRemove = () => {
    setDocumentArray([
      ...documentArray.filter(
        document => document.idDocument !== localDocumentObject.idDocument
      ),
    ]);
  };

  const handlePerEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentArray(
      documentArray.map(document =>
        document.idDocument === documentObject.idDocument
          ? { ...document, perEmail: event.target.checked }
          : document
      )
    );
  };

  if (justButtons) {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        <IconButton title='Vorschau' onClick={handleOpenPreview}>
          <FindInPage />
        </IconButton>
        {localDocumentObject.idDocument > 0 && downloadedArray && (
          <IconButton
            title='Anzahl Downloads'
            onClick={() => setIsOpenDownloaded(true)}
          >
            <Search />
          </IconButton>
        )}
        <IconButton title='Datei herunterladen' onClick={handleDownload}>
          <Download />
        </IconButton>
        {allowRemove && (
          <IconButton title='Datei löschen' onClick={handleRemove}>
            <Delete />
          </IconButton>
        )}
      </>
    );
  } else if (isStandarddocument) {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        <TableRow>
          <TableCell>
            {localDocumentObject.idDocument < 0 ? (
              <>Neu</>
            ) : (
              localDocumentObject.idDocument
            )}
          </TableCell>
          {downloadedArray && (
            <TableCell>
              {
                downloadedArray.filter(
                  document =>
                    document.idDocument === localDocumentObject.idDocument
                ).length
              }
            </TableCell>
          )}
          <TableCell>{localDocumentObject.FileName}</TableCell>
          <TableCell>
            {!!documentObject.isFromTemplate ? <CheckIcon /> : <CloseIcon />}
          </TableCell>
          <TableCell>
            <Checkbox
              checked={!!documentObject.perEmail}
              disabled={!isSendEmailPossible}
              onChange={handlePerEmailChange}
            />
          </TableCell>

          <TableCell>
            {enableSidepanel && (
              <Tooltip title='Bild-in-Bild öffnen'>
                <IconButton
                  onClick={async () => {
                    if (localDocumentObject.Data) {
                      setSelectedSidepanelDocument(localDocumentObject);
                    } else {
                      setSelectedSidepanelDocument(localDocumentObject);

                      getFetch(
                        '/document/',
                        localDocumentObject.idDocument,
                        (localDocumentObject: IDocument) => {
                          setLocalDocumentObject(localDocumentObject);
                          setSelectedSidepanelDocument(localDocumentObject);
                        }
                      );
                    }
                  }}
                >
                  <PictureInPicture />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Vorschau'>
              <IconButton onClick={handleOpenPreview}>
                <FindInPage />
              </IconButton>
            </Tooltip>
            {localDocumentObject.idDocument > 0 && downloadedArray && (
              <Tooltip title='Anzahl Downloads'>
                <IconButton onClick={() => setIsOpenDownloaded(true)}>
                  <Search />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Datei herunterladen'>
              <IconButton onClick={handleDownload}>
                <Download />
              </IconButton>
            </Tooltip>

            {(allowRemove || localDocumentObject.idDocument < 0) && (
              <Tooltip title='Datei löschen'>
                <IconButton onClick={handleRemove}>
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </>
    );
  } else {
    return (
      <>
        <DocumentDownloadedDialog
          open={isOpenDownloaded}
          onClose={() => setIsOpenDownloaded(false)}
          localDocumentObject={localDocumentObject}
          assignedLegalClients={assignedLegalClients}
        />
        <DocumentPreviewDialog
          open={isOpenPreview}
          localDocumentObject={localDocumentObject}
          onClose={() => setisOpenPreview(false)}
        />
        <TableRow>
          {checkedBoxes && setCheckedBoxes && (
            <TableCell>
              <Checkbox
                checked={
                  checkedBoxes.find(x => x === documentObject.idDocument) !==
                  undefined
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    setCheckedBoxes(prev => [
                      ...prev,
                      documentObject.idDocument,
                    ]);
                  } else {
                    setCheckedBoxes(prev => [
                      ...prev.filter(x => x !== documentObject.idDocument),
                    ]);
                  }
                }}
              />
            </TableCell>
          )}
          <TableCell>
            {localDocumentObject.idDocument < 0 ? (
              <>Neu</>
            ) : (
              localDocumentObject.idDocument
            )}
          </TableCell>
          {downloadedArray && (
            <TableCell>
              {
                downloadedArray.filter(
                  document =>
                    document.idDocument === localDocumentObject.idDocument
                ).length
              }
            </TableCell>
          )}
          <TableCell>{localDocumentObject.FileName}</TableCell>
          <TableCell>
            {enableSidepanel && (
              <Tooltip title='Bild-in-Bild öffnen'>
                <IconButton
                  onClick={async () => {
                    if (localDocumentObject.Data) {
                      setSelectedSidepanelDocument(localDocumentObject);
                    } else {
                      setSelectedSidepanelDocument(localDocumentObject);

                      getFetch(
                        '/document/',
                        localDocumentObject.idDocument,
                        (localDocumentObject: IDocument) => {
                          setLocalDocumentObject(localDocumentObject);
                          setSelectedSidepanelDocument(localDocumentObject);
                        }
                      );
                    }
                  }}
                >
                  <PictureInPicture />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Vorschau'>
              <IconButton onClick={handleOpenPreview}>
                <FindInPage />
              </IconButton>
            </Tooltip>
            {localDocumentObject.idDocument > 0 && downloadedArray && (
              <Tooltip title='Anzahl Downloads'>
                <IconButton onClick={() => setIsOpenDownloaded(true)}>
                  <Search />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Datei herunterladen'>
              <IconButton onClick={handleDownload}>
                <Download />
              </IconButton>
            </Tooltip>

            {(allowRemove || localDocumentObject.idDocument < 0) && (
              <Tooltip title='Datei löschen'>
                <IconButton onClick={handleRemove}>
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>
      </>
    );
  }
};
